/**
 * map-controls.ts — Apple/Google-Maps-style floating zoom + compass controls.
 *
 * Why:
 *   Pinch + scroll-wheel zoom are great when they work, but we have two real
 *   user populations who need a tap target instead:
 *     1. Decision-makers on a desktop touchpad with no scroll-wheel reflex —
 *        they want a +/- they can see and tap.
 *     2. Mobile users in a hurry: pinching while reading a panel is fiddly,
 *        a thumb-sized [+] in the bottom-right is the Apple/Google standard
 *        and they expect it.
 *   The compass is the third leaf: when the user accidentally rotates the
 *   map (two-finger twist, or the city default bearing of -17°), they need
 *   a one-tap "north up" — exactly Apple Maps' compass affordance.
 *
 * Design rules:
 *   - DOM-only. No GL layer interaction. Adds three buttons in a vertical
 *     stack at bottom-right.
 *   - Mobile-first: 44px hit targets (Apple HIG), generous gap, no hover
 *     dependency.
 *   - Compass rotates with map.bearing in real time — visual feedback that
 *     matches the actual camera state.
 *   - Tapping compass eases bearing to 0 with a 350ms easing — fast enough
 *     to feel snappy, slow enough to read as "rotated, not jumped".
 *   - Stays out of the way on small viewports: bottom-right corner with a
 *     mobile-safe inset (env(safe-area-inset-bottom)).
 *   - Hides itself when any full-screen overlay is open (filter page,
 *     picks page, school-chart modal). Same reactive convention as
 *     incident-bubbles.ts so we don't fight other UI for the corner.
 *
 * Failure mode:
 *   If MapLibre's `getBearing` / `easeTo` aren't available (e.g. a future
 *   downgrade) we degrade silently — buttons still render but compass-tap
 *   becomes a no-op. The map continues to work.
 */

import type { Map as MLMap } from "maplibre-gl";
import { BP_MOBILE } from "./tokens";

const ROOT_ID = "hyperlocal-map-controls";

export function installMapControls(map: MLMap): void {
  // Idempotency: a city-toggle navigation rebuilds the page, but if some
  // other path called us twice we'd duplicate the buttons.
  if (document.getElementById(ROOT_ID)) return;

  ensureStyles();

  const root = document.createElement("div");
  root.id = ROOT_ID;
  root.className = "hyperlocal-mc-root";
  // ARIA: this is a group of map controls. Give the wrapper a role so
  // assistive tech doesn't read each button as a free-floating control.
  root.setAttribute("role", "group");
  root.setAttribute("aria-label", "Map controls");

  const zoomIn = makeButton("+", "Zoom in", "hyperlocal-mc-zoom-in");
  const zoomOut = makeButton("−", "Zoom out", "hyperlocal-mc-zoom-out");
  const compass = makeButton("", "Reset rotation to north", "hyperlocal-mc-compass");
  // Monoline north-arrow icon — currentColor so it inherits whatever
  // tint the parent button uses. The previous dual-tone red/white SVG
  // (Apple-Maps style) was the only colored thing in the bottom bar
  // at idle and visually competed with the systemRed active tab tint.
  // Rotation behaviour is preserved: the inline `transform: rotate()`
  // applied by `onRotate` still works on the wrapping <svg>.
  compass.innerHTML = `
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
         stroke="currentColor" stroke-width="1.8"
         stroke-linecap="round" stroke-linejoin="round"
         aria-hidden="true">
      <polygon points="3 11 22 2 13 21 11 13 3 11"/>
    </svg>`;

  zoomIn.addEventListener("click", () => {
    try {
      // easeTo({zoom: getZoom()+1}) reads the same path as the wheel zoom
      // so the pitch-by-zoom listener sees a continuous transition.
      map.easeTo({ zoom: map.getZoom() + 1, duration: 220 });
    } catch (err) {
      console.warn("[HYPERLOCAL] zoom-in failed:", err);
    }
  });
  zoomOut.addEventListener("click", () => {
    try {
      map.easeTo({ zoom: map.getZoom() - 1, duration: 220 });
    } catch (err) {
      console.warn("[HYPERLOCAL] zoom-out failed:", err);
    }
  });
  compass.addEventListener("click", () => {
    try {
      // Reset bearing AND pitch to the city default. We pick 0 bearing
      // ("north up") because that's the universal expectation; pitch we
      // leave to whatever the zoom-by-pitch listener chooses next frame
      // (so an Apple-Maps-tilted view at z=18 stays tilted, just facing
      // north). Setting pitch to 0 here was tempting but felt jarring.
      map.easeTo({ bearing: 0, duration: 350 });
    } catch (err) {
      console.warn("[HYPERLOCAL] compass-reset failed:", err);
    }
  });

  // Live-rotate the compass icon with map bearing so it's a real indicator.
  // Throttle via rAF so we don't thrash style on every wheel tick — one
  // update per frame is enough at 60fps.
  let pendingBearingFrame = 0;
  const onRotate = (): void => {
    if (pendingBearingFrame) return;
    pendingBearingFrame = requestAnimationFrame(() => {
      pendingBearingFrame = 0;
      try {
        const b = map.getBearing();
        // CSS `transform: rotate(-bearing)` so when map rotates 30° clockwise
        // the compass arrow visually counter-rotates (north stays "up" from
        // the viewer's perspective until they hit the reset).
        const inner = compass.querySelector("svg") as SVGElement | null;
        if (inner) inner.style.transform = `rotate(${-b}deg)`;
      } catch {
        /* swallow — compass is decoration */
      }
    });
  };
  map.on("rotate", onRotate);
  map.on("rotateend", onRotate);
  // Initial sync (city default may be -17°)
  onRotate();

  // Hide when a full-screen overlay covers the map. The body data-* flags
  // are already used by incident-bubbles for the same pattern.
  installOverlayObserver(root);

  root.appendChild(zoomIn);
  root.appendChild(zoomOut);
  root.appendChild(compass);
  document.body.appendChild(root);
}

function makeButton(
  text: string,
  ariaLabel: string,
  className: string
): HTMLButtonElement {
  const b = document.createElement("button");
  b.className = `hyperlocal-mc-btn ${className}`;
  b.type = "button";
  b.setAttribute("aria-label", ariaLabel);
  b.textContent = text;
  return b;
}

let stylesInstalled = false;
function ensureStyles(): void {
  if (stylesInstalled) return;
  stylesInstalled = true;
  const style = document.createElement("style");
  style.id = "hyperlocal-mc-styles";
  style.textContent = `
.hyperlocal-mc-root {
  position: fixed;
  /* Keep above the FAB row but below the verdict card and the modal stack. */
  z-index: 1100;
  right: 14px;
  bottom: calc(14px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none; /* root passes through; buttons opt back in */
}
.hyperlocal-mc-btn {
  pointer-events: auto;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.10);
  background: rgba(28,28,28,0.82);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  color: #f1f1f1;
  font: 20px/1 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow:
    0 4px 12px rgba(0,0,0,0.30),
    inset 0 1px 0 rgba(255,255,255,0.06);
  transition: background 140ms ease, transform 140ms ease, box-shadow 140ms ease;
  user-select: none;
}
.hyperlocal-mc-btn:hover {
  background: rgba(45,45,45,0.92);
}
.hyperlocal-mc-btn:active {
  transform: scale(0.96);
  box-shadow: 0 1px 4px rgba(0,0,0,0.30) inset;
}
.hyperlocal-mc-compass svg { transition: transform 60ms linear; }

/* Hidden state when an overlay covers the map. */
body[data-mobile-filters="open"] .hyperlocal-mc-root,
body[data-school-chart-open="1"] .hyperlocal-mc-root {
  opacity: 0;
  pointer-events: none;
  transition: opacity 180ms ease;
}

@media ${BP_MOBILE} {
  .hyperlocal-mc-root {
    /* Lift above the mobile bottom-bar / FAB row */
    bottom: calc(78px + env(safe-area-inset-bottom));
  }
  .hyperlocal-mc-btn {
    width: 40px;
    height: 40px;
    border-radius: 10px;
  }
}
`;
  document.head.appendChild(style);
}

/**
 * Watches body[data-*] attributes and adds/removes a hidden class on the
 * controls. The CSS handles the actual fade. This keeps the JS dumb.
 */
function installOverlayObserver(root: HTMLElement): void {
  // No JS work needed — the CSS selectors above already handle the body
  // attribute states. We keep this hook in case a future overlay needs
  // imperative work (e.g. tabindex management). For now it's a no-op.
  void root;
}
